<template>
  <div class="access-control-page">
    <rmr-page-hero
      title="Gestion"
      title-accent="des accès"
      subtitle="Pilotez les comptes, les rôles et les permissions."
      :icon="icons.mdiShieldAccount"
    >
      <template #actions>
        <rmr-action-button
          label="Actualiser"
          :icon="icons.mdiRefresh"
          variant="translucent"
          size="sm"
          :loading="loadingPage"
          :spin-icon="loadingPage"
          @click="refreshAll"
        />
        <rmr-action-button
          :label="activeTabConfig.cta"
          :icon="icons.mdiPlus"
          size="sm"
          @click="openCreateModal"
        />
      </template>
    </rmr-page-hero>

    <div class="access-control-page__body">
      <v-card class="access-control-page__shell">
        <div class="access-control-page__toolbar">
          <div>
            <div class="access-control-page__eyebrow">Administration</div>
            <div class="access-control-page__heading">
              {{ activeTabConfig.heading }}
            </div>
            <div class="access-control-page__subheading">
              {{ activeTabConfig.description }}
            </div>
          </div>

          <rmr-tabs
            v-model="activeTabIndex"
            :tabs="tabs"
            class="access-control-page__tabs"
          />
        </div>

        <div class="access-control-page__panel">
          <v-tabs-items v-model="activeTabIndex" class="access-control-page__tab-panels">
            <v-tab-item>
              <users-table
                ref="usersTable"
                @edit="openEditModal('users', $event)"
              />
            </v-tab-item>
            <v-tab-item>
              <roles-table
                ref="rolesTable"
                @edit="openEditModal('roles', $event)"
              />
            </v-tab-item>
            <v-tab-item>
              <permissions-table
                ref="permissionsTable"
                @edit="openEditModal('permissions', $event)"
              />
            </v-tab-item>
          </v-tabs-items>
        </div>
      </v-card>
    </div>

    <rmr-modal
      v-model="modal.open"
      variant="form"
      :icon="modalConfig.icon"
      eyebrow="Gestion des accès"
      :title="modalConfig.title"
      :subtitle="modalConfig.subtitle"
      max-width="760"
      @close="closeModal"
    >
      <template #body>
        <v-card-text class="pa-6">
          <user-form
            v-if="modal.entity === 'users'"
            :key="`users-${modalFormKey}`"
            :value="modal.record"
            :roles="referenceData.roles"
            :sites="referenceData.sites"
            :site-groups="referenceData.siteGroups"
            :saving="modal.saving"
            @cancel="closeModal"
            @save="saveUser"
          />
          <role-form
            v-else-if="modal.entity === 'roles'"
            :key="`roles-${modalFormKey}`"
            :value="modal.record"
            :permissions="referenceData.permissions"
            :saving="modal.saving"
            @cancel="closeModal"
            @save="saveRole"
          />
          <permission-form
            v-else-if="modal.entity === 'permissions'"
            :key="`permissions-${modalFormKey}`"
            :value="modal.record"
            :saving="modal.saving"
            @cancel="closeModal"
            @save="savePermission"
          />
        </v-card-text>
      </template>
    </rmr-modal>
  </div>
</template>

<script>
import axios from "@axios";
import store from "@/store";
import {
  mdiAccountGroupOutline,
  mdiKeyChainVariant,
  mdiPlus,
  mdiRefresh,
  mdiShieldAccount,
} from "@mdi/js";
import UsersTable from "@/components/admin/users/users/UsersTable.vue";
import RolesTable from "@/components/admin/users/roles/RolesTable.vue";
import PermissionsTable from "@/components/admin/users/permissions/PermissionsTable.vue";
import UserForm from "@/components/admin/users/users/UserForm.vue";
import RoleForm from "@/components/admin/users/roles/RoleForm.vue";
import PermissionForm from "@/components/admin/users/permissions/PermissionForm.vue";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrModal from "@/components/ui/layout/RmrModal.vue";
import RmrPageHero from "@/components/ui/layout/RmrPageHero.vue";
import RmrTabs from "@/components/ui/layout/RmrTabs.vue";

const TAB_ORDER = ["users", "roles", "permissions"];

export default {
  name: "AccessControl",
  components: {
    PermissionsTable,
    PermissionForm,
    RmrActionButton,
    RmrModal,
    RmrPageHero,
    RmrTabs,
    RoleForm,
    RolesTable,
    UserForm,
    UsersTable,
  },
  data() {
    return {
      loadingPage: false,
      activeTabIndex: 0,
      modal: {
        open: false,
        entity: "users",
        record: null,
        saving: false,
        formKey: 0,
      },
      referenceData: {
        roles: [],
        permissions: [],
        sites: [],
        siteGroups: [],
      },
      icons: {
        mdiAccountGroupOutline,
        mdiKeyChainVariant,
        mdiPlus,
        mdiRefresh,
        mdiShieldAccount,
      },
    };
  },
  computed: {
    tabs() {
      return [
        {
          key: "users",
          label: "Utilisateurs",
          icon: this.icons.mdiAccountGroupOutline,
        },
        {
          key: "roles",
          label: "Rôles",
          icon: this.icons.mdiShieldAccount,
        },
        {
          key: "permissions",
          label: "Permissions",
          icon: this.icons.mdiKeyChainVariant,
        },
      ];
    },
    activeTab() {
      return TAB_ORDER[this.activeTabIndex] || "users";
    },
    activeTabConfig() {
      const config = {
        users: {
          heading: "Utilisateurs",
          description: "Gérez les comptes, leurs rôles et les accès associés.",
          cta: "Créer un utilisateur",
        },
        roles: {
          heading: "Rôles",
          description: "Structurez les accès métier avec des rôles réutilisables.",
          cta: "Créer un rôle",
        },
        permissions: {
          heading: "Permissions",
          description: "Définissez les actions fines utilisées par les rôles.",
          cta: "Créer une permission",
        },
      };

      return config[this.activeTab];
    },
    modalConfig() {
      const titlePrefix = this.modal.record?.id ? "Modifier" : "Créer";
      const config = {
        users: {
          icon: this.icons.mdiAccountGroupOutline,
          title: `${titlePrefix} un utilisateur`,
          subtitle: "Nom, email, mot de passe et assignation multi-rôles.",
        },
        roles: {
          icon: this.icons.mdiShieldAccount,
          title: `${titlePrefix} un rôle`,
          subtitle: "Nom métier, description et permissions associées.",
        },
        permissions: {
          icon: this.icons.mdiKeyChainVariant,
          title: `${titlePrefix} une permission`,
          subtitle: "Subject, action et description pour la sécurité applicative.",
        },
      };

      return config[this.modal.entity];
    },
    modalFormKey() {
      return `${this.modal.entity}-${this.modal.record?.id || "create"}-${this.modal.formKey}`;
    },
  },
  async created() {
    await this.loadReferenceData();
  },
  methods: {
    async loadReferenceData() {
      this.loadingPage = true;
      try {
        const [
          rolesResponse,
          permissionsResponse,
          sitesResponse,
          siteGroupsResponse,
        ] = await Promise.all([
          axios.get("/roles/"),
          axios.get("/permissions/"),
          axios.get("/sites/?sort_by=name"),
          axios.get("/site-groups/?limit=1000"),
        ]);
        this.referenceData.roles = rolesResponse.data.items || [];
        this.referenceData.permissions = permissionsResponse.data.items || [];
        this.referenceData.sites = sitesResponse.data.items || [];
        this.referenceData.siteGroups = siteGroupsResponse.data.items || [];
      } finally {
        this.loadingPage = false;
      }
    },
    openCreateModal() {
      this.modal = {
        open: true,
        entity: this.activeTab,
        record: null,
        saving: false,
        formKey: this.modal.formKey + 1,
      };
    },
    async openEditModal(entity, record) {
      this.modal = {
        open: true,
        entity,
        record: null,
        saving: false,
        formKey: this.modal.formKey + 1,
      };

      const id = record?.id;
      if (!id) {
        return;
      }

      const endpoints = {
        users: `/users/${id}`,
        roles: `/roles/${id}`,
        permissions: `/permissions/${id}`,
      };

      const { data } = await axios.get(endpoints[entity]);
      this.modal.record = data;
    },
    closeModal() {
      this.modal = {
        ...this.modal,
        open: false,
        record: null,
        saving: false,
        formKey: this.modal.formKey + 1,
      };
    },
    async saveUser(payload) {
      await this.saveEntity({
        entity: "users",
        createRequest: () => axios.post("/users/", payload),
        updateRequest: () => axios.put(`/users/${this.modal.record.id}`, payload),
        successMessage: this.modal.record?.id
          ? "Utilisateur mis à jour"
          : "Utilisateur créé",
      });
    },
    async saveRole(payload) {
      await this.saveEntity({
        entity: "roles",
        createRequest: () => axios.post("/roles/", payload),
        updateRequest: () => axios.put(`/roles/${this.modal.record.id}`, payload),
        successMessage: this.modal.record?.id ? "Rôle mis à jour" : "Rôle créé",
      });
    },
    async savePermission(payload) {
      await this.saveEntity({
        entity: "permissions",
        createRequest: () => axios.post("/permissions/", payload),
        updateRequest: () => axios.put(`/permissions/${this.modal.record.id}`, payload),
        successMessage: this.modal.record?.id
          ? "Permission mise à jour"
          : "Permission créée",
      });
    },
    async saveEntity({ entity, createRequest, updateRequest, successMessage }) {
      this.modal.saving = true;
      try {
        if (this.modal.record?.id) {
          await updateRequest();
        } else {
          await createRequest();
        }

        await this.loadReferenceData();
        await this.refreshRelevantTables(entity);
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: successMessage,
          color: "success",
          timeout: 3500,
        });
        this.closeModal();
      } finally {
        this.modal.saving = false;
      }
    },
    async refreshRelevantTables(entity) {
      const tableRefs = {
        users: ["usersTable"],
        roles: ["rolesTable", "usersTable"],
        permissions: ["permissionsTable", "rolesTable"],
      };

      await Promise.all(
        (tableRefs[entity] || []).map((refName) => this.$refs[refName]?.reload?.())
      );
    },
    async refreshAll() {
      this.loadingPage = true;
      try {
        await this.loadReferenceData();
        await Promise.all([
          this.$refs.usersTable?.reload?.(),
          this.$refs.rolesTable?.reload?.(),
          this.$refs.permissionsTable?.reload?.(),
        ]);
      } finally {
        this.loadingPage = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.access-control-page {
  background:
    radial-gradient(circle at top right, rgba($rmr-rpm, 0.08), transparent 22%),
    linear-gradient(180deg, $rmr-surface-2 0%, $rmr-surface-0 240px);
  min-height: 100%;
}

.access-control-page__body {
  padding: 24px;
}

.access-control-page__shell {
  background: rgba(255, 255, 255, 0.84) !important;
  backdrop-filter: blur(10px);
  border: 1px solid $rmr-border-hover !important;
  border-radius: $rmr-radius-2xl !important;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08) !important;
  margin-top: -18px;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.access-control-page__toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 18px;
  align-items: end;
  padding: 18px 22px;
  border-bottom: 1px solid $rmr-border-hover;
}

.access-control-page__eyebrow {
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.9px;
  color: $rmr-indigo;
}

.access-control-page__heading {
  font-size: 24px;
  font-weight: 800;
  color: $rmr-indigo-dark;
  margin-top: 4px;
}

.access-control-page__subheading {
  margin-top: 6px;
  color: $rmr-text-muted;
  font-size: 13px;
}

.access-control-page__tabs {
  min-width: min(100%, 420px);
}

.access-control-page__panel {
  padding: 18px 22px 22px;
}

@media (max-width: 960px) {
  .access-control-page__body {
    padding: 16px;
  }

  .access-control-page__toolbar {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
}
</style>
